import { z } from 'zod'
import { type UserData, type WithUserId } from '../user'
import { type LocationData } from '../locations/model'
import { LatLngSchema, type LatLng } from '../locations/latLng'
import {
  type CompositeKeyId,
  CompositeKeyIdSchema,
  idSchemaV1,
  isId,
  type LocationId,
  LocationIdSchema,
  TeamIdSchema,
  type UserId,
  UserIdSchema,
} from '../ids'
import { TinyUserSchema, type TinyUser } from '../entity'
import { DispositionIdSchema, type DispositionId } from '../disposition'
import { type WithCompanyId } from '../company'
import { AuditPropsSchema, type AuditProps } from '../common'
import { TinyAddressSchema } from '../address'

export const ContactIdSchema = idSchemaV1('Contact')
export type ContactId = z.infer<typeof ContactIdSchema>
export const isContactId = isId(ContactIdSchema)

export const CompositeContactIdSchema = z.union([ContactIdSchema, CompositeKeyIdSchema])
export type CompositeContactId = ContactId | CompositeKeyId

export const ContactKeySchema = z.object({
  pk: z.string().meta({ deprecated: true }),
  sk: z.string().meta({ deprecated: true }),
})
export type ContactKey = z.infer<typeof ContactKeySchema>

export type ContactDate = number

export const CONTACT_METHODS = ['door', 'phone'] as const
export const ContactMethodSchema = z.enum(CONTACT_METHODS).meta({
  id: 'contactMethod',
  description: 'The method of contact for the contact.',
})
export type ContactMethod = z.infer<typeof ContactMethodSchema>

const UnsavedContactDataSchema = z.object({
  locationId: LocationIdSchema,
  disposition: DispositionIdSchema,
  latitude: LatLngSchema.shape.latitude.meta({ description: 'latitude of the device location' }),
  longitude: LatLngSchema.shape.longitude.meta({ description: 'longitude of the device location' }),
  pitch: z
    .boolean()
    .optional()
    .meta({ description: "does this contact qualify as a pitch, leave blank to use the disposition's default" }),
  method: ContactMethodSchema.optional(),
  contactDate: z.number().optional(),
  contactDay: z.string().meta({ description: 'ISO 8601 string' }),
  source: z.string().optional(),
  importId: z.string().optional(), // TODO: should this be importId schema
  contactId: CompositeContactIdSchema.optional(),
  teamId: TeamIdSchema.optional(),
  pitchErrors: z.string().array().optional(),
})
export type UnsavedContactData = z.infer<typeof UnsavedContactDataSchema>

export type UnsavedLeadPayload = {
  readonly locationId: LocationId
  readonly deviceLocation: LatLng
}

export const UnsavedLeadDataSchema = z.object({
  payload: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().optional(),
  email: z.string().optional(),
  notes: z.string().optional(),
  method: ContactMethodSchema.optional(),
  location: z.object({
    ...LatLngSchema.shape,
    ...TinyAddressSchema.shape,
  }),
  source: z.string().optional(),
  teamId: TeamIdSchema.optional(),
})
export type UnsavedLeadData = z.infer<typeof UnsavedLeadDataSchema>

export const ContactDataSchema = z.object({
  ...ContactKeySchema.shape,
  ...UnsavedContactDataSchema.shape,
  ...AuditPropsSchema.shape,
  contactId: CompositeContactIdSchema,
  setterId: UserIdSchema,
  setter: TinyUserSchema.optional(),
  contactDate: z.number(),
  line1: z.string(),
  line2: z.string(),
  locationCoordinates: LatLngSchema.optional().meta({
    description: 'The coordinates of the location',
  }),
  distanceFromProperty: z.number().optional(),
})
export type ContactData = z.infer<typeof ContactDataSchema>

export type ExternalContactData = Pick<ContactData, 'locationId' | 'disposition' | 'pitch' | 'method'> & {
  contactDate: string
  setter: TinyUser
  location: {
    line1: string
    line2: string
    latitude?: number
    longitude?: number
    resident?: {
      firstName?: string
      lastName?: string
      phone?: string
      email?: string
    }
  }
  latitude: number
  longitude: number
  appointmentDate?: string
  updatedAt: number
}

export type UnsavedExternalContactData = AuditProps & {
  ownerUserId: UserId
  disposition: DispositionId
  location: LatLng & {
    line1?: string
    line2?: string
    city?: string
    state?: string
    zip?: string
  }
  resident?: {
    firstName?: string
    lastName?: string
    phone?: string
    email?: string
  }
  notes?: string
  contactDate: number
  source: string
}
export type ContactSaveResult = {
  result: string
  original: UnsavedExternalContactData
  createdContact?: UnsavedContactData
  createdContactPartitionKey?: string
  createdContactSortKey?: string
  attemptedLocations?: LocationData[]
  locationConfidence?: number
  user?: UserData
}

export type ContactImportWithUserCompany = ContactImport & WithUserId & WithCompanyId

export type ContactImport = {
  raw: string
  line1?: string
  unitNbr?: string
  unitType?: string
  locality?: string
  countrySubd?: string
  postal1?: string
  latitude?: number
  longitude?: number
  setter?: UserId
  disposition?: string
  contactDate?: ContactDate
  notes?: string
  expiresAt?: number
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
}
